import React from 'react';

import { isValid, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { updateAllDatesAction, updateTravelStartDateAction } from 'store/itineraryDetails';
import { getItineraryScheduleTypeSelector, getScheduleItemsSelector } from 'store/itineraryDetails/selectors';
import { confirmAlert } from 'utils/alert';
import { getLocalDateFromString } from 'utils/date';
import { formatDate } from 'utils/itineraryDetails';
import { SCHEDULE_TYPES } from 'utils/itineraryDetails/constants';
import { isDefined } from 'utils/ramda';

import style from './index.module.css';

export const useChooseFirstDate = () => {
  const initialState = 'No Date';
  const scheduleType = useSelector(getItineraryScheduleTypeSelector);
  const scheduleItems = useSelector(getScheduleItemsSelector);
  const [currentDate, setCurrentDate] = React.useState(initialState);
  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const startDate =
    currentDate === initialState || !isDefined(currentDate.datesRange)
      ? initialState
      : formatDate(currentDate.datesRange.start);
  const buttonContainerRef = React.useRef();
  const datePickerRef = React.useRef();
  const dispatch = useDispatch();
  const disabled =
    scheduleItems.every((el) => !isDefined(el.datesRange.start) || !isDefined(el.datesRange)) ||
    scheduleType === SCHEDULE_TYPES.DAYS;

  const updateTravelStartDate = () => {
    let minimalDate = scheduleItems
      .filter((el) => isDefined(el.datesRange) && isDefined(el.datesRange.start))
      .reduce(
        function (a, b) {
          return a.datesRange.start < b.datesRange.start ? a : b;
        },
        { datesRange: { start: null } },
      );
    if (minimalDate.datesRange.start === null || scheduleType === SCHEDULE_TYPES.DAYS) {
      return setCurrentDate(initialState);
    }
    setCurrentDate(minimalDate);

    if (isValid(parseISO(minimalDate.datesRange.start))) {
      dispatch(updateTravelStartDateAction(minimalDate.datesRange.start));
    }
  };

  React.useEffect(() => {
    updateTravelStartDate();
  }, [scheduleItems]);

  const openUpdatePopup = () => {
    setIsPopupOpen(true);
  };

  const finallyUpdateDate = (range, value) => {
    let maxDate = scheduleItems
      .filter((el) => isDefined(el.datesRange.start))
      .reduce(function (a, b) {
        if (scheduleItems.every((el) => el.datesRange.end === null)) {
          return (a.datesRange.start || '') > (b.datesRange.start || '') ? a : b;
        }
        return (a.datesRange.end || '') > (b.datesRange.end || '') ? a : b;
      });

    const newTravelStartDate = (() => {
      if (
        (!maxDate.datesRange.end && maxDate.datesRange.start > range.start && !range.end) ||
        (currentDate.type === 'activity' && currentDate.datesRange.start > range.start) ||
        (maxDate.datesRange.end && maxDate.datesRange.start > range.start && !range.end) ||
        (!currentDate.datesRange.end && !maxDate.datesRange.end && !range.end)
      ) {
        return range.start;
      }

      if (
        (!maxDate.datesRange.end && maxDate.datesRange.start <= range.end) ||
        (!maxDate.datesRange.end && maxDate.datesRange.start > range.end) ||
        (currentDate.type === 'activity' && currentDate.datesRange.start < range.end && range.end) ||
        (!currentDate.datesRange.end && maxDate.datesRange.end >= range.end) ||
        (!currentDate.datesRange.end && maxDate.datesRange.end < range.end)
      ) {
        return range.end;
      }

      return range.start;
    })();

    const formattedNewTravelStartDate = formatDate(getLocalDateFromString(newTravelStartDate));

    if (value !== 0) {
      confirmAlert({
        noBorderWithMargin: true,
        title: (
          <span className={style.headsUp}>
            <div className={style.modalText}>Heads up!</div>
            Are you sure you want to update the Start Date for this itinerary?
            <br />
            All subsequent dates will be updated with a new <br />
            start date of <strong>{formattedNewTravelStartDate}</strong>
          </span>
        ),
        confirmButtonText: 'Confirm',
        onConfirm: () => {
          setCurrentDate({
            ...currentDate,
            datesRange: {
              start:
                (currentDate.type === 'activity' &&
                  currentDate.datesRange.end &&
                  currentDate.datesRange.end < range.end) ||
                ((currentDate.type === 'activity' || currentDate.type === 'day_room') &&
                  !currentDate.datesRange.end &&
                  currentDate.datesRange.start < range.end)
                  ? range.end
                  : range.start,
              end: null,
            },
          });

          dispatch(updateAllDatesAction({ dateDifference: value, travelStartDate: newTravelStartDate }));
        },
      });
    }
    setIsPopupOpen(false);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  React.useEffect(() => {
    let minimalDate = scheduleItems.reduce(function (a, b) {
      const isADefined = isDefined(a.datesRange) && isDefined(a.datesRange.start);
      const isBDefined = isDefined(b.datesRange) && isDefined(b.datesRange.start);
      if (isADefined && isBDefined) {
        return a.datesRange.start < b.datesRange.start ? a : b;
      }
      if (isADefined && !isBDefined) {
        return a;
      }
      if (!isADefined && isBDefined) {
        return b;
      }
      return initialState;
    }, []);

    return setCurrentDate(minimalDate);
  }, [scheduleType]);

  React.useEffect(() => {
    if (disabled) {
      setCurrentDate(initialState);
    }
  }, [disabled]);

  React.useEffect(() => {
    if (datePickerRef) {
      datePickerRef.current = {
        open: openUpdatePopup,
      };
    }
  }, []);

  return {
    initialState,
    scheduleType,
    scheduleItems,
    currentDate,
    setCurrentDate,
    isPopupOpen,
    startDate,
    buttonContainerRef,
    datePickerRef,
    disabled,
    openUpdatePopup,
    finallyUpdateDate,
    handleClosePopup,
  };
};
